import React, { useMemo } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import * as actions from "../../_redux/unregistered-students/UnregisteredStudentsAction";
import {
  DeleteButton,
  ExportButton,
  FormButton,
  copy,
  deletionAlert,
  exportToExcel,
  exportToPDF,
  getAuthorities,
  noRecordsAlert,
  print,
  redirectToPages,
  resultAlert
} from "../../../../../_helpers/Helpers";
import { 
  Card, 
  CardBody, 
  CardHeader,
  CardHeaderToolbar,
  ModalProgressBar
} from "../../../../../_metronic/_partials/controls";
import { UnregisteredStudentsFilter } from "./unregistered-students-filter/UnregisteredStudentsFilter";
import { UnregisteredStudentsTable } from "./unregistered-students-table/UnregisteredStudentsTable";
import { useUnregisteredStudentsUIContext } from "./UnregisteredStudentsUIContext";

export function UnregisteredStudentsCard() {
  // Unregistered Students UI Context
  const unregisteredStudentsUIContext = useUnregisteredStudentsUIContext();
  const unregisteredStudentsUIProps = useMemo(() => {
    return {
      ids: unregisteredStudentsUIContext.ids,
      setIds: unregisteredStudentsUIContext.setIds,
      queryParams: unregisteredStudentsUIContext.queryParams,
      openNewStudentPage: unregisteredStudentsUIContext.openNewStudentPage,
    };
  }, [unregisteredStudentsUIContext]);
    
  // Getting curret state of Unregistered Student list from store (Redux)
  const { currentState } = useSelector(
    (state) => ({ currentState: state.unregisteredStudents }),
    shallowEqual
  );
  const { actionsLoading, isSuccess, type, authorities } = currentState;

  // Authority Parameters
  const { isExportationAuthorized, isAdditionAuthorized, isDeletionAuthorized } = getAuthorities(authorities);
  
  // Unregistered Students Redux state
  const dispatch = useDispatch();

  /// Yetki ve Hata Kontrolleri
  if (isSuccess === false) return redirectToPages(type);

  /** Belli Bir Okul Dışı Öğrenci Listesini Excele Aktarır */
  const exportUnregisteredStudentsToExcel = () => {
    dispatch(actions.getAllUnregisteredStudents(unregisteredStudentsUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const rows = [];
      const widths = [20, 25, 25, 20, 50, 30, 25, 50];
      const columns = ["TC Kimlik No", "Adı", "Soyadı", "Sınıfı", "Okulu", "Veli Adı Soyadı", "Veli Telefonu", "Açıklama"];
      
      entities.forEach(entity => rows.push([
        entity.tcKimlikNo,
        entity.name,
        entity.surname,
        entity.grade,
        entity.school,
        entity.parentName,
        entity.parentPhone,
        entity.explanation
      ]));
      
      const filename = unregisteredStudentsUIProps.queryParams.term + " - " + (unregisteredStudentsUIProps.queryParams.term + 1) + " Eğitim Öğretim Yılı Okul Dışı Öğrenci Listesi";
      const sheetname = "Okul Dışı Öğrenci Listesi";
      exportToExcel({ columns, rows, widths, filename, sheetname });
    });
  }

  /** Belli Bir Okul Dışı Öğrenci Listesini PDF e Aktarır */
  const exportUnregisteredStudentsToPDF = () => {
    dispatch(actions.getAllUnregisteredStudents(unregisteredStudentsUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const views = [ 
        {
          header: "TC Kimlik No",
          rows: entities.map(entity => entity.tcKimlikNo)
        },
        {
          header: "Adı",
          rows: entities.map(entity => entity.name)
        },
        {
          header: "Soyadı",
          rows: entities.map(entity => entity.surname)
        },
        {
          header: "Sınıfı",
          rows: entities.map(entity => entity.grade)
        },
        {
          header: "Okulu",
          rows: entities.map(entity => entity.school)
        },
        {
          header: "Veli Adı Soyadı",
          rows: entities.map(entity => entity.parentName)
        },
        {
          header: "Veli Telefonu",
          rows: entities.map(entity => entity.parentPhone)
        },
        {
          header: "Açıklama",
          rows: entities.map(entity => entity.explanation)
        }
      ];
  
      const filename = unregisteredStudentsUIProps.queryParams.term + " - " + (unregisteredStudentsUIProps.queryParams.term + 1) + " Eğitim Öğretim Yılı Okul Dışı Öğrenci Listesi";
      exportToPDF(views, filename);
    });
  }

  /** Belli Bir Okul Dışı Öğrenci Listesini Yazdırma Penceresini Açar */
  const printUnregisteredStudents = () => {
    dispatch(actions.getAllUnregisteredStudents(unregisteredStudentsUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const data = [];    
      const header = unregisteredStudentsUIProps.queryParams.term + " - " + (unregisteredStudentsUIProps.queryParams.term + 1) + " Eğitim Öğretim Yılı Okul Dışı Öğrenci Listesi";
      const properties = ["TC Kimlik No", "Adı", "Soyadı", "Sınıfı", "Okulu", "Veli Adı Soyadı", "Veli Telefonu", "Açıklama"];
  
      entities.forEach(entity => {
        data.push({
          "TC Kimlik No": entity.tcKimlikNo,
          "Adı": entity.name,
          "Soyadı": entity.surname,
          "Sınıfı": entity.grade,
          "Okulu": entity.school,
          "Veli Adı Soyadı": entity.parentName,
          "Veli Telefonu": entity.parentPhone,
          "Açıklama": entity.explanation
        });
      });
  
      print({ data, properties, header });
    });    
  }

  /** Belli Bir Okul Dışı Öğrenci Listesini Panoya Kopyalar */
  const copyUnregisteredStudents = () => {
    dispatch(actions.getAllUnregisteredStudents(unregisteredStudentsUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const rows = [];
      const columns = ["TC Kimlik No", "Adı", "Soyadı", "Sınıfı", "Okulu", "Veli Adı Soyadı", "Veli Telefonu", "Açıklama"];
      const header = unregisteredStudentsUIProps.queryParams.term + " - " + (unregisteredStudentsUIProps.queryParams.term + 1) + " Eğitim Öğretim Yılı Okul Dışı Öğrenci Listesi";
      entities.forEach(entity => rows.push([
        entity.tcKimlikNo,
        entity.name,
        entity.surname,
        entity.grade,
        entity.school,
        entity.parentName,
        entity.parentPhone,
        entity.explanation
      ]));
      copy({ header, columns, rows });
    });
  }

  /** Belli Bir Okul Dışı Öğrenci Listesini Siler */
  const deleteUnregisteredStudents = (ids) => {
    if (typeof ids === 'string') ids = ids.split(",");

    if (ids.length < 1) {
      noRecordsAlert();
      return;
    }  
    deletionAlert().then(result => {
      if (result.isConfirmed) dispatch(actions.deleteUnregisteredStudents(ids)).then(result => onResult(result));
    });
  }

  const onResult = ({ isSuccess, type, message }) => {
    resultAlert(type, message);
  
    if (isSuccess) {
      setTimeout(() => {
        unregisteredStudentsUIProps.setIds([]);
        dispatch(actions.getUnregisteredStudents(unregisteredStudentsUIProps.queryParams));
      }, 2500);
    }
  };
  
  const title = <>Okul Dışı Öğrenci Listesi{unregisteredStudentsUIProps.ids.length > 0 ?
                  <> [Seçilen Kayıt Sayısı: <span style={{color:"red"}}>{unregisteredStudentsUIProps.ids.length}</span>]</> :
                  ""}
                </>;

  return (
    <Card>
      {actionsLoading && <ModalProgressBar />}
      <CardHeader title={title}>
        {
          (isExportationAuthorized || isAdditionAuthorized || isDeletionAuthorized) &&
          <CardHeaderToolbar>
            {
              isExportationAuthorized &&
              <ExportButton
                exportToExcel={exportUnregisteredStudentsToExcel}
                exportToPDF={exportUnregisteredStudentsToPDF}
                print={printUnregisteredStudents}
                copy={copyUnregisteredStudents}
              />
            }
            {
              isAdditionAuthorized &&
              <FormButton 
                path="Navigation/Plus"
                title="Yeni Öğrenci"
                method={unregisteredStudentsUIProps.openNewStudentPage}
              />
            }
            {
              isDeletionAuthorized &&
              <DeleteButton 
                title="Seçilenleri Sil"
                method={() => deleteUnregisteredStudents(unregisteredStudentsUIProps.ids)}
              />
            }
          </CardHeaderToolbar>
        }        
      </CardHeader>
      <CardBody>
        <UnregisteredStudentsFilter />
        <UnregisteredStudentsTable
          deleteUnregisteredStudents={deleteUnregisteredStudents}
        />
      </CardBody>
    </Card>
  );
}